import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/features/auth';
import { useLayout } from '@/contexts/LayoutContext';
import { usePlugins } from '@/contexts/PluginContext';
import { UserMenu } from '../UserMenu';
import { NotificationBell } from '@/features/notifications/components/NotificationBell';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { TodayCalendarWidget } from '../TodayCalendarWidget';
import { useSystemSettings } from '@/contexts/SystemSettingsContext';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboardIcon,
  UsersIcon,
  FolderIcon,
  SettingsIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BookmarkCheck,
  CalendarDays,
  DoorOpen,
  PackageIcon,
} from 'lucide-react';

/**
 * SidebarLayout Component
 * Side navigation layout with collapsible sidebar
 */
export default function SidebarLayout() {
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { sidebarCollapsed, toggleSidebar } = useLayout();
  const { isPluginEnabled, plugins, loading } = usePlugins();
  const { settings } = useSystemSettings();

  const isActive = (path: string) => location.pathname.startsWith(path);

  // Base navigation items (conditional based on role)
  // NOTE: Dashboard/Users/Resources hidden from main navigation as of user request
  // Routes still work if accessed directly. Can be restored by uncommenting below.
  // These items are still accessible via the Admin dashboard.
  const baseNavItems = [];

  // HIDDEN: Admin users see all pages (commented out - items hidden from navigation)
  // if (user?.role === 'ADMIN' || user?.role === 'SUPERADMIN') {
  //   baseNavItems.push(
  //     { path: '/dashboard', icon: LayoutDashboardIcon, label: 'Dashboard' },
  //     { path: '/users', icon: UsersIcon, label: 'Users' },
  //     { path: '/resources', icon: FolderIcon, label: 'Resources' }
  //   );
  // }
  // Regular users don't see these pages in navigation

  // Plugin-based navigation items
  const pluginNavItems = [];
  // Check for 'BOOKING' plugin type (matches database seed: apps/backend/prisma/migrations/0002_seed_users.sql)
  if (isPluginEnabled('BOOKING')) {
    pluginNavItems.push(
      { path: '/bookings/my-bookings', icon: BookmarkCheck, label: t('navigation.myBookings') },
      { path: '/calendar', icon: CalendarDays, label: t('navigation.calendar') },
      { path: '/bookings/rooms', icon: DoorOpen, label: t('navigation.bookRoom') }
    );
  }
  // Check for 'BOOKING' plugin type (unified booking system)
  if (isPluginEnabled('BOOKING')) {
    pluginNavItems.push(
      { path: '/bookings/equipment', icon: PackageIcon, label: t('navigation.bookEquipment') }
    );
  }

  // Combine all navigation items: base + plugins
  // Note: Settings and Admin removed from navbar (now in UserMenu)
  const navItems = [...baseNavItems, ...pluginNavItems];

  return (
    <div
      className="min-h-screen flex transition-colors duration-300"
      style={{
        backgroundColor: 'var(--theme-bg)',
        color: 'var(--theme-text)'
      }}
    >
      {/* Sidebar */}
      <aside
        className={`
          ${sidebarCollapsed ? 'w-20' : 'w-64'}
          sticky top-0 h-screen z-40
          flex flex-col
          transition-all duration-300 ease-in-out
        `}
        style={{
          backgroundColor: 'var(--theme-surface)',
          borderRight: `var(--theme-border-width) solid var(--theme-border)`,
          boxShadow: 'var(--theme-shadow)'
        }}
      >
        {/* Logo/Brand */}
        <div className="h-16 flex items-center justify-center border-b transition-all duration-300"
          style={{
            borderColor: 'var(--theme-border)'
          }}
        >
          {settings?.navbarLogoData ? (
            <img
              src={settings.navbarLogoData}
              alt={settings.appName || 'Logo'}
              className={`transition-all duration-300 object-contain ${
                sidebarCollapsed ? 'h-8 w-8' : (
                  settings.navbarLogoSize === 'sm' ? 'h-7 w-auto max-w-[200px]' :
                  settings.navbarLogoSize === 'lg' ? 'h-21 w-auto max-w-[200px]' :
                  'h-14 w-auto max-w-[200px]'  // md default
                )
              }`}
              style={{
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
              }}
            />
          ) : (
            <span
              className={`text-2xl font-bold transition-all duration-300 ${sidebarCollapsed ? 'text-xl' : ''}`}
              style={{
                color: 'var(--theme-accent)',
                fontWeight: 'var(--theme-font-weight-heading)'
              }}
            >
              {sidebarCollapsed
                ? (settings?.appName?.[0] || 'C')
                : (settings?.appName || 'Chamama Booking')
              }
            </span>
          )}
        </div>

        {/* Navigation Links */}
        <nav className="typo-navigation flex-1 px-3 py-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  typo-navigation flex items-center gap-3 px-3 py-2.5 rounded-lg
                  transition-all duration-200
                  ${active ? 'font-semibold bg-[var(--theme-accent)]/10' : 'font-medium'}
                  ${sidebarCollapsed ? 'justify-center' : ''}
                `}
                style={{
                  color: active ? 'var(--theme-accent)' : 'var(--theme-text-muted)',
                }}
                title={sidebarCollapsed ? item.label : undefined}
              >
                <Icon className={`${sidebarCollapsed ? 'h-6 w-6' : 'h-5 w-5'} flex-shrink-0`} />
                {!sidebarCollapsed && <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Today's Calendar Widget - Only show when sidebar is expanded */}
        {!sidebarCollapsed && <TodayCalendarWidget />}

        {/* Collapse/Expand Button */}
        <button
          onClick={toggleSidebar}
          className="h-10 flex items-center justify-center border-t transition-colors duration-200 hover:opacity-70"
          style={{
            borderColor: 'var(--theme-border)',
            backgroundColor: 'var(--theme-surface)',
            color: 'var(--theme-text-muted)'
          }}
        >
          {sidebarCollapsed ? (
            <ChevronRightIcon className="h-5 w-5" />
          ) : (
            <ChevronLeftIcon className="h-5 w-5" />
          )}
        </button>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar (minimal) */}
        <header
          className="sticky top-0 z-50 h-16 flex items-center justify-between px-6 border-b transition-all duration-300 flex-shrink-0 gap-4"
          style={{
            backgroundColor: 'var(--theme-surface)',
            borderColor: 'var(--theme-border)',
            boxShadow: 'var(--theme-shadow)'
          }}
        >
          <h1
            className="text-xl font-semibold min-w-0 flex-1"
            style={{
              color: 'var(--theme-text)',
              fontWeight: 'var(--theme-font-weight-heading)'
            }}
          >
            {/* Page title could go here */}
          </h1>

          {/* Notification Bell, Language Switcher, and User Menu - flex-shrink-0 prevents being pushed off-screen in RTL */}
          {user && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <NotificationBell />
              <LanguageSwitcher />
              <UserMenu />
            </div>
          )}
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto page-background-crt">
          <div className="max-w-7xl mx-auto py-6 px-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
